import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import {
  BANGKOK_ZONES,
  type BangkokDistrict,
  type BangkokZone,
} from "@/content/bangkok/districts";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import type { MetroArea } from "@/content/bangkok/metro-faq";

type Faq = { q: string; a: string };
type BangkokIndexData = {
  grouped: Record<BangkokZone, BangkokDistrict[]>;
  districtCount: number;
  metroAreas: MetroArea[];
  faqs: Faq[];
};

export const Route = createFileRoute("/bangkok/")({
  component: BangkokIndex,
  loader: async (): Promise<BangkokIndexData> => {
    const [{ BANGKOK_DISTRICTS, districtsByZone }, metro, wave2] = await Promise.all([
      import("@/content/bangkok/districts"),
      import("@/content/bangkok/metro-faq"),
      import("@/content/bangkok/metro-faq-wave2"),
    ]);
    return {
      grouped: districtsByZone(),
      districtCount: BANGKOK_DISTRICTS.length,
      metroAreas: metro.BANGKOK_METRO_AREAS,
      faqs: [...metro.BANGKOK_METRO_FAQ_TH, ...wave2.BANGKOK_METRO_FAQ_TH_WAVE2],
    };
  },
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const districts = loaderData
      ? Object.values(loaderData.grouped).flat()
      : [];
    const base = pageHeadForProvince({
      title: `Notary Public กรุงเทพ — ครอบคลุม 50 เขต BTS/MRT`,
      description: `บริการ Notary, แปลรับรอง, MFA, สถานทูต และวีซ่าครอบคลุมทั้ง ${loaderData?.districtCount ?? 50} เขตของกรุงเทพ — ใกล้ทุกสถานี BTS, MRT, ARL และย่านธุรกิจหลัก`,
      path: "/bangkok",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "พื้นที่บริการ กรุงเทพ", url: "/bangkok" },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Bangkok Districts Served",
            itemListElement: districts.map((d, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `${site}/bangkok/${d.slug}`,
              name: `${d.nameTh} (${d.nameEn})`,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildFaqSchema(loaderData?.faqs ?? [])),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(buildSpeakableSchema("/bangkok")),
        },
      ],
    };
  },
});

function BangkokIndex() {
  const {
    grouped,
    districtCount,
    metroAreas: BANGKOK_METRO_AREAS,
    faqs: BANGKOK_METRO_FAQ_TH,
  } = Route.useLoaderData() as BangkokIndexData;
  const zoneOrder: BangkokZone[] = [
    "silom-sathorn",
    "sukhumvit",
    "central",
    "rama9-ratchada",
    "north",
    "east",
    "thonburi-west",
    "south",
    "old-town",
  ];
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-6xl px-4 py-14">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              Service Coverage
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">
              Notary, แปล, MFA และวีซ่า — ครอบคลุมทุกเขตในกรุงเทพ
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
              เลือกเขตของคุณเพื่อดูบริการ on-site — ทีมทนายและทีมแปลเดินทางถึงคอนโด สำนักงาน หรือโรงแรมในเขตคุณภายในวันเดียวกัน รองรับเคสด่วน ใกล้ทุกสถานี BTS, MRT, ARL และย่านธุรกิจหลักรวม {districtCount} เขต.
            </p>
          </div>
        </section>

        <div className="mx-auto max-w-6xl space-y-12 px-4 py-14">
          {zoneOrder.map((z) => {
            const list = grouped[z];
            if (!list?.length) return null;
            return (
              <section key={z}>
                <h2 className="text-2xl font-black text-primary">
                  {BANGKOK_ZONES[z].th}
                  <span className="ml-2 text-sm font-medium text-muted-foreground">
                    {BANGKOK_ZONES[z].en} • {list.length} เขต
                  </span>
                </h2>
                <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {list.map((d) => (
                    <Link
                      key={d.slug}
                      to="/bangkok/$district"
                      params={{ district: d.slug }}
                      className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-gold"
                    >
                      <div className="text-[10px] font-bold uppercase tracking-widest text-gold">
                        {d.postalCodes.join(" / ")}
                      </div>
                      <h3 className="mt-1 text-base font-extrabold text-primary group-hover:text-gold">
                        {d.nameTh}{" "}
                        <span className="text-xs font-normal text-muted-foreground">
                          {d.nameEn}
                        </span>
                      </h3>
                      <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                        {d.intro}
                      </p>
                      <div className="mt-3 text-[11px] font-semibold text-gold">
                        🚉 {d.transit.slice(0, 2).join(", ")}
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}

          <section id="metro">
            <h2 className="text-2xl font-black text-primary">
              ปริมณฑล
              <span className="ml-2 text-sm font-medium text-muted-foreground">
                Bangkok Metropolitan Region • {BANGKOK_METRO_AREAS.length} จังหวัด
              </span>
            </h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {BANGKOK_METRO_AREAS.map((m) => (
                <Link
                  key={m.slug}
                  to="/provinces/$province"
                  params={{ province: m.slug }}
                  className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-gold"
                >
                  <h3 className="text-base font-extrabold text-primary group-hover:text-gold">
                    {m.nameTh}{" "}
                    <span className="text-xs font-normal text-muted-foreground">
                      {m.nameEn}
                    </span>
                  </h3>
                  <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-muted-foreground">
                    {m.intro}
                  </p>
                  <div className="mt-3 text-[11px] font-semibold text-gold">
                    📍 {m.hubs.slice(0, 3).join(", ")}
                  </div>
                  <div className="mt-1 text-[11px] text-muted-foreground">
                    {m.immigration}
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <section aria-label="คำถามที่พบบ่อย กรุงเทพและปริมณฑล">
            <h2 className="text-2xl font-black text-primary">
              คำถามที่พบบ่อย — กรุงเทพฯ และปริมณฑล ({BANGKOK_METRO_FAQ_TH.length} ข้อ)
            </h2>
            <div className="mt-5 space-y-3">
              {BANGKOK_METRO_FAQ_TH.map((f, i) => (
                <details
                  key={i}
                  className="rounded-2xl border border-border bg-card p-5"
                  {...(i < 3 ? { open: true } : {})}
                >
                  <summary className="cursor-pointer text-sm font-bold text-primary">
                    {f.q}
                  </summary>
                  <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
